// Kiểu dữ liệu vật phẩm (ITEMS §11). Chỉ phần P0 dùng; trường P1+ để tùy chọn.
import type { Element } from '../elements';
import type { Phase } from '../screens';

export const EQUIP_SLOTS = [
  'weapon',
  'helm',
  'robe',
  'pants',
  'gloves',
  'boots',
  'necklace',
  'ring1',
  'ring2',
  'treasure',
] as const;
export type EquipSlot = (typeof EQUIP_SLOTS)[number];
/** Loại ô của phôi: hai ô nhẫn dùng chung loại `ring`. */
export type SlotKind = Exclude<EquipSlot, 'ring1' | 'ring2'> | 'ring';

export const WEAPON_TYPES = ['sword', 'saber', 'spear', 'zither', 'fan', 'whisk', 'seal', 'gourd'] as const;
export type WeaponType = (typeof WEAPON_TYPES)[number];

export const RARITIES = ['mortal', 'yellow', 'mystic', 'earth', 'heaven', 'immortal', 'chaos'] as const;
export type Rarity = (typeof RARITIES)[number];

export type AffixTier = 5 | 4 | 3 | 2 | 1;
export type ScaleLetter = 'S' | 'A' | 'B' | 'C' | 'D' | 'E';
export type TriKey = 'body' | 'spirit' | 'mind';
export type CoDuyenId =
  | 'defiant_heart'
  | 'still_light_vase'
  | 'devouring_body'
  | 'supreme_bone'
  | 'bronze_ring'
  | 'spring_autumn'
  | 'fate_eye';

/** Đơn vị dòng: ×P(Giai), ×Giai, %, ms, điểm. */
export type AffixUnit = 'xP' | 'xGiai' | 'pct' | 'ms' | 'pt';

/** Chỉ số tổng hợp mà dòng cộng vào (để tính Lực Chiến). `null` = chưa vào LC ở P0. */
export type StatKey =
  | 'atkFlat'
  | 'atkPct'
  | 'hpFlat'
  | 'hpPct'
  | 'def'
  | 'critRate'
  | 'critDmg'
  | 'triBody'
  | 'triSpirit'
  | 'triMind';

export interface AffixDef {
  readonly id: string;
  readonly slots: readonly SlotKind[];
  readonly unit: AffixUnit;
  /** Khoảng theo bậc, chỉ số mảng = bậc − 1 (T1 ở đầu); T1[1] = Cực. */
  readonly ranges: readonly (readonly [number, number])[];
  /** Ô "đúng vai" (trọng số AFFIX_ROLE_WEIGHT). */
  readonly roleSlots?: readonly SlotKind[];
  readonly stat: StatKey | null;
  readonly phase: Phase;
}

export interface BaseDef {
  readonly id: string;
  readonly slot: SlotKind;
  readonly weaponType?: WeaponType;
  /** Icon trong public/assets/items (không đuôi); không có thì ô dùng placeholder. */
  readonly icon?: string;
  /** Dòng gốc: id affix + giá trị (đơn vị của affix; xP / xGiai nhân lúc tính). */
  readonly implicit: readonly { readonly affix: string; readonly value: number }[];
  readonly scaling?: Readonly<Record<TriKey, ScaleLetter>>;
  readonly element?: Element;
  readonly signatureOf?: string;
  readonly phase: Phase;
}

export interface DaoGlyphDef {
  readonly id: string;
  readonly group: CoDuyenId | 'common';
  readonly trigger: string;
  readonly byLevel: readonly [number, number, number, number, number];
  readonly sources: readonly string[];
  readonly phase: Phase;
}

export interface UniqueDef {
  readonly id: string;
  readonly base: string;
  readonly rarity: 'immortal';
  readonly fixedAffixes: readonly string[];
  readonly icon: string;
  readonly source: string;
  readonly firstKillGuaranteed?: boolean;
  /** Biến thể Tiền Chủ theo cờ: `flag=value` → hậu tố khóa i18n. */
  readonly formerOwnerVariants?: readonly { readonly when: string; readonly suffix: string }[];
  readonly phase: Phase;
}

export interface ItemLine {
  affix: string;
  tier: AffixTier;
  /** Nấc 0..AFFIX_ROLL_NOTCHES−1 trong khoảng của bậc; nấc cuối của T1 = Cực. */
  notch: number;
  /** Giá trị theo đơn vị của affix, chưa nhân P / Giai. */
  value: number;
}

/** Món đồ lưu trong save (ITEMS §11 `ItemInstance`, phần P0). */
export interface ItemInstance {
  uid: string;
  base: string;
  rarity: Rarity;
  giai: number;
  lines: ItemLine[];
  daoGlyph?: { id: string; level: number };
  uniqueId?: string;
  enhance: number;
  enhanceFailStacks: number;
  /** Nguồn rơi (khóa i18n) + mốc nhận, cho dải nguồn gốc ở khối ④. */
  source: string;
  gotAtMs: number;
  /** Người chơi khóa (không tự phân giải). */
  locked?: boolean;
}
